import React, { useState, useEffect } from "react";
import { Form, Row, Col, Button } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../store";
import { AIChatbotFilters } from "../../../features/ai_chatbots/aiChatbotsService";
import { useTranslation } from "react-i18next";
import { getCompanies } from "../../../features/companies/companiesSlice";

interface AIChatbotsFilterProps {
  onFilterChange: (filters: AIChatbotFilters) => void;
}

const AIChatbotsFilter: React.FC<AIChatbotsFilterProps> = ({ onFilterChange }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<any>();
  const { filters } = useSelector((state: RootState) => state.aiChatbots);
  const { companies } = useSelector((state: RootState) => state.companies);
  const { selectedAgencyId, selectedClientId } = useSelector((state: RootState) => state.settings);
  const { user } = useSelector((state: RootState) => state.auth);

  const [localFilters, setLocalFilters] = useState<AIChatbotFilters>({
    company_id: selectedClientId || selectedAgencyId || undefined,
    status: undefined,
    is_active: undefined,
    search: "",
  });

  useEffect(() => {
    // Component mount olduğunda veya agency/client seçimleri değiştiğinde varsayılanı ayarla
    setLocalFilters((prev) => ({
      ...prev,
      company_id: selectedClientId || selectedAgencyId || undefined,
    }));
  }, [selectedAgencyId, selectedClientId]);

  // Şirket listesi yoksa ya da seçili şirket listede yoksa, şirketleri yükle
  useEffect(() => {
    const needsFetch = !companies || companies.length === 0;
    const missingSelected = localFilters.company_id
      ? !companies?.some(c => c.id === localFilters.company_id)
      : false;
    if (needsFetch || missingSelected) {
      dispatch(getCompanies({ limit: 1000 }));
    }
  }, [dispatch, companies, localFilters.company_id]);

  useEffect(() => {
    // Redux'taki filtreler değişince UI'ı güncelle (özellikle sayfa yenilemelerinde)
    if (filters) {
      setLocalFilters((prev) => ({
        ...prev,
        ...filters,
      }));
    }
  }, [filters]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    if (name === "company_id") {
      setLocalFilters({
        ...localFilters,
        company_id: value ? Number(value) : undefined,
      });
    } else if (name === "is_active") {
      setLocalFilters({
        ...localFilters,
        is_active: value === "" ? undefined : value === "true",
      });
    } else {
      setLocalFilters({
        ...localFilters,
        [name]: value,
      } as AIChatbotFilters);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // company_id'yi number'a çevir
    const filtersToSend = {
      ...localFilters,
      company_id: localFilters.company_id ? Number(localFilters.company_id) : undefined
    };
    onFilterChange(filtersToSend);
  };

  const handleReset = () => {
    const resetFilters: AIChatbotFilters = {
      company_id: selectedClientId || selectedAgencyId || undefined,
      status: undefined,
      is_active: undefined,
      search: "",
    };
    setLocalFilters(resetFilters);
    // company_id'yi number'a çevir
    const filtersToSend = {
      ...resetFilters,
      company_id: resetFilters.company_id ? Number(resetFilters.company_id) : undefined
    };
    onFilterChange(filtersToSend);
  };

  // Kullanıcı rollerini kontrol et
  const userRoles = user?.roles || [];
  const isMarketingSpecialist = userRoles.includes('marketing_specialist');
  const isAgencyOwner = userRoles.includes('agency_owner');
  const isAdmin = userRoles.includes('admin') || userRoles.includes('superadmin');

  // Kullanıcının erişebileceği şirketleri filtrele
  const availableCompanies = companies.filter(company => {
    // Varsayılan: client şirketleri göster
    const isClient = company.company_type === "client";
    // Seçili agency ID'si varsa ve bu şirket agency ise, onu da listeye dahil et ki select değeri görünsün
    const isSelectedAgency = selectedAgencyId && company.id === selectedAgencyId;

    if (!(isClient || isSelectedAgency)) {
      return false;
    }

    if (isAdmin) {
      return true;
    } else if (isAgencyOwner) {
      return true; // TODO: Sadece kendi client'larını göster
    } else if (isMarketingSpecialist) {
      return true; // TODO: Sadece bağlı olduğu client'ları göster
    }
    return false;
  });

  // Not: Şirket listesi henüz yüklenmediyse, seçenek geçici olarak boş görünebilir;
  // liste yüklendiğinde seçili company_id otomatik olarak görünür olacaktır.

  return (
    <Form onSubmit={handleSubmit}>
      <Row>
        <Col md={4}>
          <Form.Group className="mb-2">
            <Form.Label>{t("client_company")}</Form.Label>
            <Form.Select 
              name="company_id" 
              value={localFilters.company_id !== undefined ? String(localFilters.company_id) : ""}
              onChange={handleInputChange}
            >
              <option value="">{t("all_client_companies")}</option>
              {availableCompanies.map((company) => (
                <option key={company.id} value={company.id}>
                  {company.company_name}
                </option>
              ))}
            </Form.Select>
            <Form.Text className="text-muted">
              {t("select_client_company_to_filter_chatbots")}
            </Form.Text>
          </Form.Group>
        </Col>

        <Col md={4}>
          <Form.Group className="mb-2">
            <Form.Label>{t("active_status")}</Form.Label>
            <Form.Select name="is_active" value={localFilters.is_active?.toString() || ""} onChange={handleInputChange}>
              <option value="">{t("all")}</option>
              <option value="true">{t("active")}</option>
              <option value="false">{t("inactive")}</option>
            </Form.Select>
          </Form.Group>
        </Col>

        <Col md={4}>
          <Form.Group className="mb-2">
            <Form.Label>{t("search")}</Form.Label>
            <Form.Control
              type="text"
              name="search"
              placeholder={t("search_placeholder")}
              value={localFilters.search || ""}
              onChange={handleInputChange}
            />
          </Form.Group>
        </Col>

        <Col md={12} className="d-flex justify-content-end mt-2">
          <Button variant="secondary" type="button" onClick={handleReset} className="me-2">
            {t("reset")}
          </Button>
          <Button variant="primary" type="submit">
            {t("apply_filters")}
          </Button>
        </Col>
      </Row>
    </Form>
  );
};

export default AIChatbotsFilter;